import React, { useEffect, useMemo, useRef } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { Spin } from 'antd';
import { observer } from 'mobx-react-lite';
import { SideBar } from '@/components/SideBar/SideBar';
import { Presence } from '@/components/Presence/Presence';
import {
  ActivityHistory,
  CallRecord,
  CallPage,
  ChatDetails,
  Dialer,
  DialerView,
  DialPad,
  Entities,
  Inbox,
  Initialization,
  Login,
  MarketingLists,
  Settings,
  Voicemail,
} from '@/pages';
import { MAIN } from '@/configs/main.config';
import { LOCAL_STORAGE } from '@/shared/constants/localStorage';
import { DialerControl } from '@/components/DialerControl/DialerControl';
import { useStore } from '@/stores';
import { useNotification } from '@/hooks/useNotifications';
import { useDynEvents } from '@/hooks/useDynEvents';
import { useAppNavigate } from '@/hooks/useAppNavigate';
import { useLogin } from '@/hooks/useLogin';
import { useChats } from '@/hooks/useChats';
import { useVoicemails } from '@/hooks/useVoicemails';
import { usePhoneCalls } from '@/hooks/usePhoneCalls';
import { Inactive } from '@/pages/Inactive';
import { SolgariApiService } from '@/services/solgari-api/solgari-api.service';
import { SipJs } from '@/services/sip-js.service';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import { ForceReload } from '@/components/ForceReload/ForceReload';
import { CallDetails } from '@/pages';
import { Phonebook } from '@/pages';
import { MinimizedCallBar } from '../MinimizedCallBar/MinimizedCallBar';
import { MinimizedWrapUpBar } from '../MinimizedWrapUpBar/MinimizedWrapUpBar';
import { CALL_PAGE_TYPE } from '@/shared/constants/callPage';
import '../../App.scss';

interface StartScreenProps {
  dynVersion: number;
}

const StartScreen = observer(({ dynVersion }: StartScreenProps) => {
  const DEV_MODE = localStorage.getItem(MAIN.LS_IS_DEV);

  const {
    dialerStore: { isDialerActive },
    chatsStore: { globalSpinner },
    settingsStore: { setSettings, tabSettings, setActiveTabId },
    callStore: { callPage },
  } = useStore();

  const { page, status } = callPage;

  const { voicemailList } = useVoicemails();
  const navigate = useRef(useNavigate());
  const appRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const sideBarRef = useRef<HTMLDivElement>(null);
  const currentTabId = tabSettings.currentTabId;

  const {
    navigateTo,
    checkAndOpenIfPluginMinimized,
    setViewForPlugin,
    showSideBar,
    location,
  } = useAppNavigate({
    navigate,
    appRef,
  });
  const { sipDevLogin, runLogin, isLogged, setIsLogged, presenceStatus } =
    useLogin();

  const { maximizeCallPage } = usePhoneCalls({
    setIsLogged,
    setViewForPlugin,
    navigate,
    checkAndOpenIfPluginMinimized,
    runLogin,
    currentTabId,
  });

  useChats({ dynVersion, navigateTo });
  useNotification({ setSettings });
  useDynEvents();

  // cachedActiveTabId - active tab id that is stored in local storage and shared among other opened tabs
  // document.hidden - determine current document state (if we are currently on the page)
  const checkAppStatus = () => {
    const cachedActiveTabId = localStorage.getItem(LOCAL_STORAGE.LS_TAB_ID_KEY);
    const inactiveAppState = location.pathname === '/inactive';

    // If we are on the inactive tab and closed the tab with active state - make this inactive tab tab great again
    if (inactiveAppState && !cachedActiveTabId && !document.hidden) {
      localStorage.setItem(
        LOCAL_STORAGE.LS_TAB_ID_KEY,
        tabSettings.currentTabId,
      );
      setActiveTabId(tabSettings.currentTabId);
      navigateTo('dial-pad');
      window.location.reload();
      return;
    }

    // In case of inactive tab there is no need to constantly navigate to inactive route
    if (inactiveAppState) {
      return;
    }

    // If we open new browser tab and there is another active tab - make new tab inactive
    if (cachedActiveTabId && cachedActiveTabId !== tabSettings.currentTabId) {
      navigateTo('inactive');
    }
  };

  useEffect(() => {
    // Erase local storage active tabId and stop all active calls when you close or reload it so another tab can become active
    const onActiveTabClose = () => {
      const cachedActiveTabId = localStorage.getItem(
        LOCAL_STORAGE.LS_TAB_ID_KEY,
      );
      const isSessionActive = localStorage.getItem(
        LOCAL_STORAGE.LS_SESSION_IS_ACTIVE,
      );

      if (cachedActiveTabId === tabSettings.currentTabId) {
        localStorage.setItem(LOCAL_STORAGE.LS_TAB_ID_KEY, '');
        if (isSessionActive) {
          localStorage.removeItem(LOCAL_STORAGE.LS_SESSION_IS_ACTIVE);
          SipJs.onUnload = true;
          //TODO This part is not working as expected. We can't call async functions in 'beforeunload' event
          SipJs.declineAllCalls();
          SolgariApiService.onStop();
          SipJs.stopSip();
        }
      }
    };

    window.addEventListener('beforeunload', onActiveTabClose);
    const cachedActiveTabId = localStorage.getItem(LOCAL_STORAGE.LS_TAB_ID_KEY);

    if (cachedActiveTabId && cachedActiveTabId !== currentTabId) {
      SipJs?.stopSip();
      navigateTo('inactive');
    }

    const statusInterval = setInterval(checkAppStatus, 1000);

    return () => {
      clearInterval(statusInterval);
      window.removeEventListener('beforeunload', onActiveTabClose);
    };
  }, [tabSettings, location.pathname]);

  /**
   * Here possible use Login screen to skip dyn login
   */
  const initialScreen = useMemo(
    () =>
      DEV_MODE ? <Login onLoginHandler={sipDevLogin} /> : <Initialization />,
    [DEV_MODE, sipDevLogin],
  );

  const returnToPreviousScreen = () => {
    navigate.current(-1);
  };

  return (
    <Spin spinning={globalSpinner} tip="Loading..." size="large">
      <div
        ref={appRef}
        className={`app ${isDialerActive ? 'with-dialer' : ''} app-version-${dynVersion}`}
      >
        <div ref={panelRef} className="plugin">
          <DialerControl />
          <Presence status={presenceStatus}></Presence>
          {page === CALL_PAGE_TYPE.MINIMIZED && status === 'active' && (
            <MinimizedCallBar maximizeCallPage={maximizeCallPage} />
          )}
          {page === CALL_PAGE_TYPE.MINIMIZED && status === 'wrapup' && (
            <MinimizedWrapUpBar maximizeCallPage={maximizeCallPage} />
          )}
          <Routes>
            <Route
              path="/login"
              element={<ErrorBoundary>{initialScreen}</ErrorBoundary>}
            />
            <Route
              path="/inactive"
              element={
                <ErrorBoundary>
                  <Inactive />
                </ErrorBoundary>
              }
            />
            {isLogged && (
              <>
                <Route
                  path="/dial-pad"
                  element={
                    <ErrorBoundary>
                      <DialPad />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/call-record"
                  element={
                    <ErrorBoundary>
                      <CallRecord />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/call-details"
                  element={
                    <ErrorBoundary>
                      <CallDetails />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/inbox"
                  element={
                    <ErrorBoundary>
                      <Inbox />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/inbox/:id"
                  element={
                    <ErrorBoundary>
                      <ChatDetails />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/activity-history"
                  element={
                    <ErrorBoundary>
                      <ActivityHistory />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/activity-history/:id"
                  element={
                    <ErrorBoundary>
                      <ChatDetails />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/voicemail-list"
                  element={
                    <ErrorBoundary>
                      <Voicemail data={voicemailList} />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/entities"
                  element={
                    <ErrorBoundary>
                      <Entities />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ErrorBoundary>
                      <Settings />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/dialer"
                  element={
                    <ErrorBoundary>
                      <MarketingLists />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/dialer-view"
                  element={
                    <ErrorBoundary>
                      <DialerView />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/phonebook"
                  element={
                    <ErrorBoundary>
                      <Phonebook />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/dialer/:id"
                  element={
                    <ErrorBoundary>
                      <Dialer />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/force-reload"
                  element={
                    <ErrorBoundary>
                      <ForceReload
                        sendWarning={returnToPreviousScreen}
                        mode="show-content"
                      />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="*"
                  element={
                    <ErrorBoundary>
                      <DialPad />
                    </ErrorBoundary>
                  }
                />
              </>
            )}
          </Routes>
        </div>
        <CallPage />
        {showSideBar && (
          <div className="sidebar-wrapper" ref={sideBarRef}>
            <SideBar
              isLogged={isLogged}
              onNavigate={navigateTo}
              voicemailLength={voicemailList?.count}
            />
          </div>
        )}
      </div>
    </Spin>
  );
});

export default StartScreen;
